import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  Timestamp,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { nanoid } from "nanoid";
import { openDB, DBSchema, IDBPDatabase } from "idb";

export interface UserTemplate {
  templateId: string;
  userId: string;
  name: string;
  markdown: string;
  author: string | null;
  description: string | null;
  image: string | null;
  nodeType: string;
  createdAt: Date;
  updatedAt: Date;
  isSystem?: boolean;
}

interface TemplateDB extends DBSchema {
  templates: {
    key: string;
    value: UserTemplate;
    indexes: { userId: string };
  };
}

let dbInstance: IDBPDatabase<TemplateDB> | null = null;

async function getDB(): Promise<IDBPDatabase<TemplateDB>> {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<TemplateDB>("FocusMapTemplates", 1, {
    upgrade(db) {
      const store = db.createObjectStore("templates", {
        keyPath: "templateId",
      });
      store.createIndex("userId", "userId");
    },
  });

  return dbInstance;
}

// Extract metadata from markdown
function extractMetadata(markdown: string, field: string): string | null {
  const regex = new RegExp(`^${field}:\\s*(.+?)\\s*$`, "m");
  const match = markdown.match(regex);
  return match ? match[1].trim() : null;
}

function extractH1(markdown: string): string | null {
  const match = markdown.match(/^#\s+(.+?)$/m);
  return match ? match[1].trim() : null;
}

// Firestore operations
export async function saveUserTemplate(
  userId: string,
  templateId: string | null,
  markdown: string,
  useLocalStorage: boolean = false,
): Promise<string> {
  const id = templateId || nanoid();
  const name = extractH1(markdown) || "Untitled Template";
  const author = extractMetadata(markdown, "Author");
  const description = extractMetadata(markdown, "Description");
  const image = extractMetadata(markdown, "Image");
  const nodeType = extractMetadata(markdown, "NodeType") || "Card";
  const now = new Date();

  const template: UserTemplate = {
    templateId: id,
    userId,
    name,
    markdown,
    author,
    description,
    image,
    nodeType,
    createdAt: templateId
      ? (await loadUserTemplate(userId, id, useLocalStorage))?.createdAt || now
      : now,
    updatedAt: now,
  };

  if (useLocalStorage) {
    // Save to IndexedDB
    const db = await getDB();
    await db.put("templates", template);
  } else {
    // Save to Firestore
    const templateRef = doc(db, "user_templates", id);
    await setDoc(templateRef, {
      ...template,
      createdAt: Timestamp.fromDate(template.createdAt),
      updatedAt: Timestamp.fromDate(template.updatedAt),
    });
  }

  return id;
}

export async function loadUserTemplates(
  userId: string,
  useLocalStorage: boolean = false,
): Promise<UserTemplate[]> {
  if (useLocalStorage) {
    // Load from IndexedDB
    const db = await getDB();
    const tx = db.transaction("templates", "readonly");
    const index = tx.store.index("userId");
    const templates = await index.getAll(userId);
    return templates;
  } else {
    // Load from Firestore
    const templatesRef = collection(db, "user_templates");
    const q = query(templatesRef, where("userId", "==", userId));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        ...data,
        templateId: doc.id,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as UserTemplate;
    });
  }
}

export async function loadUserTemplate(
  userId: string,
  templateId: string,
  useLocalStorage: boolean = false,
): Promise<UserTemplate | null> {
  if (useLocalStorage) {
    // Load from IndexedDB
    const db = await getDB();
    const template = await db.get("templates", templateId);
    if (template && template.userId === userId) {
      return template;
    }
    return null;
  } else {
    // Load from Firestore
    const templateRef = doc(db, "user_templates", templateId);
    const snapshot = await getDoc(templateRef);

    if (!snapshot.exists()) return null;

    const data = snapshot.data();
    if (data.userId !== userId) return null;

    return {
      ...data,
      templateId: snapshot.id,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    } as UserTemplate;
  }
}

export async function deleteUserTemplate(
  userId: string,
  templateId: string,
  useLocalStorage: boolean = false,
): Promise<void> {
  if (useLocalStorage) {
    // Delete from IndexedDB
    const db = await getDB();
    const template = await db.get("templates", templateId);
    if (template && template.userId === userId) {
      await db.delete("templates", templateId);
    }
  } else {
    // Delete from Firestore
    const templateRef = doc(db, "user_templates", templateId);
    const snapshot = await getDoc(templateRef);

    if (snapshot.exists() && snapshot.data().userId === userId) {
      await deleteDoc(templateRef);
    }
  }
}

// File import/export operations
export async function exportTemplateToFile(
  template: UserTemplate,
): Promise<void> {
  const blob = new Blob([template.markdown], { type: "text/markdown" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${template.name.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function importTemplateFromFile(): Promise<string> {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".md,.markdown";

    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) {
        reject(new Error("No file selected"));
        return;
      }

      try {
        const text = await file.text();
        resolve(text);
      } catch (error) {
        reject(error);
      }
    };

    input.click();
  });
}

// Sync user templates between Firestore and IndexedDB
export async function syncUserTemplatesOffline(
  userId: string,
  templates: UserTemplate[],
): Promise<void> {
  const db = await getDB();
  const tx = db.transaction("templates", "readwrite");

  // Clear existing templates for this user
  const index = tx.store.index("userId");
  const existingKeys = await index.getAllKeys(userId);
  for (const key of existingKeys) {
    await tx.store.delete(key);
  }

  // Add new templates
  for (const template of templates) {
    await tx.store.put(template);
  }

  await tx.done;
}

export async function loadUserTemplatesOffline(
  userId: string,
): Promise<UserTemplate[]> {
  return loadUserTemplates(userId, true);
}
